import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { searchTermsApi, type SearchTermsScope } from '../api/searchTermsApi';
import { seoKeys } from '../queryKeys';

// Keyed by location only: the practice and agency routes serve the same SearchTermSet, so a save from
// either view refreshes both.
export function useSearchTerms(scope: SearchTermsScope) {
  return useQuery({
    queryKey: seoKeys.searchTerms(scope.locationId),
    queryFn: () => searchTermsApi.get(scope),
    enabled: !!scope.locationId,
  });
}

/** Suggested core services from Search Console. Nested under the search-terms key, so a save
 *  refreshes it too. */
export function useSearchTermSuggestions(scope: SearchTermsScope) {
  return useQuery({
    queryKey: seoKeys.searchTermSuggestions(scope.locationId),
    queryFn: () => searchTermsApi.suggestions(scope),
    enabled: !!scope.locationId,
  });
}

export function useSaveSearchTerms(scope: SearchTermsScope, onSaved?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (queries: string[]) => searchTermsApi.save(scope, queries),
    onSuccess: () => {
      onSaved?.();
      return queryClient.invalidateQueries({ queryKey: seoKeys.searchTerms(scope.locationId) });
    },
    onError: () => toast.error('Failed to save search terms. Try again.'),
  });
}
